import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { INR, photoUrl } from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Button } from "@/components/ui/button";
import { Zap, Eye, Users, PlusCircle, Pencil, PowerOff, Power } from "lucide-react";
import { toast } from "sonner";

export default function MyListings() {
  const nav = useNavigate();
  const [listings, setListings] = useState([]);

  const load = async () => {
    const r = await api.get("/my/listings");
    setListings(r.data.listings);
  };

  useEffect(() => { load(); }, []);

  const boost = async (id) => {
    try {
      await api.post(`/listings/${id}/boost`);
      toast.success("Boosted! Your listing now appears at the top.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Boost failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/listings/${id}/status`, null, { params: { status } });
      toast.success(status === "active" ? "Listing reactivated!" : "Listing deactivated.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const statusBadge = (l) => {
    if (l.status === "active")
      return <span className="chip chip-verified">Active</span>;
    if (l.status === "inactive")
      return <span className="chip bg-amber-100 text-amber-800">Inactive</span>;
    return <span className="chip bg-red-100 text-red-700">{l.status}</span>;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-medium">My listings</h1>
          <Button data-testid="my-listings-new" className="brand-accent rounded-full" onClick={() => nav("/post")}>
            <PlusCircle className="w-4 h-4 mr-2" /> New listing
          </Button>
        </div>

        {listings.length === 0 && (
          <div className="text-center py-16 text-stone-500 border border-dashed border-stone-300 rounded-2xl">
            No listings yet. Post your first one.
          </div>
        )}

        <div className="space-y-3">
          {listings.map((l) => (
            <div
              key={l.id}
              data-testid={`my-listing-${l.id}`}
              className="p-4 bg-white border border-stone-200 rounded-2xl flex items-center gap-4"
            >
              {/* Thumbnail */}
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                {l.photos?.[0] && (
                  <img src={photoUrl(l.photos[0])} className="w-full h-full object-cover" alt="" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => nav(`/listing/${l.id}`)}
                  className="font-medium text-stone-900 truncate block text-left hover:underline"
                >
                  {l.title}
                </button>
                <div className="text-sm text-stone-500">
                  {l.locality} · {l.bhk} · {INR(l.rent)}/mo
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {l.views || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" /> {l.enquiries_count || 0} enquiries
                  </span>
                  {statusBadge(l)}
                  {l.boosted && <span className="chip bg-purple-100 text-purple-800">⚡ Boosted</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  data-testid={`edit-${l.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => nav(`/post?id=${l.id}`)}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button
                  data-testid={`boost-${l.id}`}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => boost(l.id)}
                  disabled={l.status !== "active"}
                  title={l.status !== "active" ? "Reactivate listing to boost" : "Boost to top"}
                >
                  <Zap className="w-3 h-3 mr-1" /> Boost
                </Button>
                {l.status === "active" ? (
                  <Button
                    data-testid={`deactivate-${l.id}`}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setStatus(l.id, "inactive")}
                  >
                    <PowerOff className="w-3 h-3 mr-1" /> Deactivate
                  </Button>
                ) : (
                  <Button
                    data-testid={`reactivate-${l.id}`}
                    size="sm"
                    variant="ghost"
                    className="text-emerald-700 hover:text-emerald-800"
                    onClick={() => setStatus(l.id, "active")}
                  >
                    <Power className="w-3 h-3 mr-1" /> Reactivate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
